import { useState, useMemo, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Navigate } from 'react-router-dom'
import api from '@/lib/api'
import SlideOver from '@/components/SlideOver'
import AdvancedFilters from '@/components/AdvancedFilters'
import FilteredEmptyState from '@/components/FilteredEmptyState'
import CommentsSection from '@/components/CommentsSection'
import ProjectDocumentPanel, { DocFile } from '@/components/ProjectDocumentPanel'
import { ChevronRight, ChevronLeft, ArrowRight, Calendar, X, ExternalLink, Clock, Edit2, Check, Loader2, User } from 'lucide-react'
import AuditInfo from '@/components/AuditInfo'
import { formatDateOnly } from '@/lib/dateUtils'

type StatusColumn = 'Confirmed' | 'Procurement' | 'In-Progress' | 'Complete'
type EditRole = 'super_admin' | 'admin' | 'director' | 'sales_manager' | 'asst_sales_manager' | 'sales_executive'
type AdminRole = 'super_admin' | 'admin' | 'director'
const ADMIN_ROLES: AdminRole[] = ['super_admin', 'admin', 'director']
const ENGINEERING_ROLES = ['chief_engineer', 'operation_manager', 'engineer', 'technician']

interface PMProject {
  id: number
  project_code: string
  project_type: string
  client_name: string
  status: string
  installation_start_date: string | null
  expected_completion_date: string | null
  assigned_to: string
  team_members: { id: number; full_name: string; role: string }[] | null
  date_completed: string | null
  service_expiry_date: string | null
  created_at: string
  updated_at?: string
  created_by_name?: string
  address: string
  site_photos?: DocFile[] | null
  pre_install_docs?: DocFile[] | null
  completion_docs?: DocFile[] | null
}

interface ActivityEntry {
  id: number
  full_name: string
  action: string
  details: string
  created_at: string | null
}

interface SimpleUser {
  id: number
  full_name: string
  role: string
}

const STATUS_COLUMNS: StatusColumn[] = ['Confirmed', 'Procurement', 'In-Progress', 'Complete']
const STATUS_COLORS: Record<string, string> = {
  Confirmed: 'bg-blue-100 text-blue-700 border-blue-200',
  Procurement: 'bg-orange-100 text-orange-700 border-orange-200',
  'In-Progress': 'bg-amber-100 text-amber-700 border-amber-200',
  Complete: 'bg-green-100 text-green-700 border-green-200',
}
const STATUS_HEADER_COLORS: Record<string, string> = {
  Confirmed: 'bg-blue-600',
  Procurement: 'bg-orange-500',
  'In-Progress': 'bg-amber-500',
  Complete: 'bg-green-600',
}
const GANTT_BAR_COLORS: Record<string, string> = {
  Confirmed: 'bg-blue-400',
  Procurement: 'bg-amber-400',
  'In-Progress': 'bg-purple-500',
  Complete: 'bg-green-500',
}

const ROLE_SHORT: Record<string, string> = {
  chief_engineer: 'CE', operation_manager: 'OM', engineer: 'Eng', technician: 'Tech',
  procurement: 'Proc', storekeeper: 'SK', sales_manager: 'SM', asst_sales_manager: 'ASM', sales_executive: 'SE',
}


function parseAssignedTo(value: string): { team_leader: string; sales_person: string } {
  if (!value) return { team_leader: '', sales_person: '' }
  try {
    const p = JSON.parse(value)
    if (typeof p === 'object' && p !== null && !Array.isArray(p)) {
      return { team_leader: p.team_leader || '', sales_person: p.sales_person || '' }
    }
  } catch {}
  return { team_leader: value, sales_person: '' }
}

function DateFilter({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="text-xs font-medium text-slate-600">
      {label}
      <input type="date" value={value} onChange={e => onChange(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" />
    </label>
  )
}

function isStatusColumn(s: string): s is StatusColumn {
  return (STATUS_COLUMNS as string[]).includes(s)
}

function isAdminRole(role: string): role is AdminRole {
  return (ADMIN_ROLES as string[]).includes(role)
}

function isEditRole(role: string): role is EditRole {
  const editRoles: EditRole[] = ['super_admin', 'admin', 'director', 'sales_manager', 'asst_sales_manager', 'sales_executive']
  return (editRoles as string[]).includes(role)
}

function getNextStatus(current: string): StatusColumn | null {
  if (!isStatusColumn(current)) return null
  const idx = STATUS_COLUMNS.indexOf(current)
  if (idx < 0 || idx >= STATUS_COLUMNS.length - 1) return null
  return STATUS_COLUMNS[idx + 1]
}

function getPrevStatus(current: string): StatusColumn | null {
  if (!isStatusColumn(current)) return null
  const idx = STATUS_COLUMNS.indexOf(current)
  if (idx <= 0) return null
  return STATUS_COLUMNS[idx - 1]
}

function isTeamLeaderOf(userName: string, project: PMProject): boolean {
  const assigned = parseAssignedTo(project.assigned_to)
  return !!assigned.team_leader && userName === assigned.team_leader
}

function canEditPMProject(userRole: string, userName: string, project: PMProject): boolean {
  return isEditRole(userRole) || isTeamLeaderOf(userName, project)
}

function canMoveForward(userRole: string, userName: string, project: PMProject): boolean {
  const s = project.status
  const teamLeader = isTeamLeaderOf(userName, project)
  if (s === 'Confirmed') {
    return ['super_admin', 'admin', 'director', 'procurement', 'storekeeper', 'sales_manager', 'asst_sales_manager', 'sales_executive'].includes(userRole) || teamLeader
  }
  if (s === 'Procurement') {
    return ['super_admin', 'admin', 'director', 'procurement', 'storekeeper'].includes(userRole) || teamLeader
  }
  if (s === 'In-Progress') {
    return isAdminRole(userRole) || userRole === 'director' || teamLeader
  }
  return false
}

function canMoveBackward(userRole: string): boolean {
  return isAdminRole(userRole)
}

function isCompletingSoon(project: PMProject): boolean {
  if (project.status !== 'Complete' || !project.date_completed) return false
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const completed = new Date(project.date_completed)
  completed.setHours(0, 0, 0, 0)
  const diffMs = today.getTime() - completed.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  return diffDays >= 0 && diffDays <= 14
}

function timeAgo(isoString: string | null): string {
  if (!isoString) return ''
  const diff = Date.now() - new Date(isoString).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
}

export default function ProjectManagement() {
  const queryClient = useQueryClient()
  const user = JSON.parse(localStorage.getItem('user') || '{}')
  const userRole = user.role || ''
  const userName = user.full_name || ''

  const [view, setView] = useState<'kanban' | 'timeline'>('kanban')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [projectTypeFilter, setProjectTypeFilter] = useState('')
  const [teamLeaderFilter, setTeamLeaderFilter] = useState('')
  const [salesPersonFilter, setSalesPersonFilter] = useState('')
  const [teamMemberFilter, setTeamMemberFilter] = useState('')
  const [installationFrom, setInstallationFrom] = useState('')
  const [installationTo, setInstallationTo] = useState('')
  const [expectedCompletionFrom, setExpectedCompletionFrom] = useState('')
  const [expectedCompletionTo, setExpectedCompletionTo] = useState('')
  const [completedFrom, setCompletedFrom] = useState('')
  const [completedTo, setCompletedTo] = useState('')
  const [expiryFrom, setExpiryFrom] = useState('')
  const [expiryTo, setExpiryTo] = useState('')
  const [createdFrom, setCreatedFrom] = useState('')
  const [createdTo, setCreatedTo] = useState('')
  const [updatedFrom, setUpdatedFrom] = useState('')
  const [updatedTo, setUpdatedTo] = useState('')
  const [completedWindow, setCompletedWindow] = useState('recent')
  const [selected, setSelected] = useState<PMProject | null>(null)
  const [moveError, setMoveError] = useState('')

  const { data: settings, isLoading: settingsLoading } = useQuery<{ project_management_enabled: boolean }>({
    queryKey: ['app-settings'],
    queryFn: () => api.get('/project-management/settings').then(r => r.data),
    staleTime: 60000,
  })

  const { data: users = [] } = useQuery<SimpleUser[]>({
    queryKey: ['users-list'],
    queryFn: () => api.get('/users/list').then(r => r.data),
    staleTime: 5 * 60 * 1000,
  })
  const engineeringUsers = users.filter(u => ENGINEERING_ROLES.includes(u.role))
  const salesUsers = users.filter(u => ['sales_manager', 'asst_sales_manager', 'sales_executive'].includes(u.role))
  const advancedPmParams = {
    search: search || undefined, status: statusFilter || undefined, project_type: projectTypeFilter || undefined,
    team_leader: teamLeaderFilter || undefined, sales_person: salesPersonFilter || undefined, team_member: teamMemberFilter || undefined,
    installation_from: installationFrom || undefined, installation_to: installationTo || undefined,
    expected_completion_from: expectedCompletionFrom || undefined, expected_completion_to: expectedCompletionTo || undefined,
    completed_from: completedFrom || undefined, completed_to: completedTo || undefined,
    expiry_from: expiryFrom || undefined, expiry_to: expiryTo || undefined,
    created_from: createdFrom || undefined, created_to: createdTo || undefined,
    updated_from: updatedFrom || undefined, updated_to: updatedTo || undefined,
    completed_window: completedWindow,
  }
  const advancedPmFilterCount = Object.values(advancedPmParams).filter(value => value && value !== 'recent').length
  const resetAdvancedPmFilters = () => {
    setSearch(''); setStatusFilter(''); setProjectTypeFilter(''); setTeamLeaderFilter(''); setSalesPersonFilter(''); setTeamMemberFilter('')
    setInstallationFrom(''); setInstallationTo(''); setExpectedCompletionFrom(''); setExpectedCompletionTo('')
    setCompletedFrom(''); setCompletedTo(''); setExpiryFrom(''); setExpiryTo(''); setCreatedFrom(''); setCreatedTo(''); setUpdatedFrom(''); setUpdatedTo('')
    setCompletedWindow('recent')
  }

  const { data: projects = [], isLoading: projectsLoading, isError: projectsError } = useQuery<PMProject[]>({
    queryKey: ['pm-projects', advancedPmParams],
    queryFn: () => api.get('/project-management', { params: advancedPmParams }).then(r => r.data),
    enabled: settings?.project_management_enabled === true,
  })

  // Sync selected project when query data updates (e.g., after document upload/delete)
  useEffect(() => {
    if (selected) {
      const updated = projects.find(p => p.id === selected.id)
      if (updated) setSelected(updated)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects])

  const moveStatus = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      api.patch(`/project-management/${id}/status`, { status }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['pm-projects'] })
      queryClient.invalidateQueries({ queryKey: ['pm-activities', selected?.id] })
      setMoveError('')
      if (selected && selected.id === res.data.id) {
        setSelected(res.data)
      }
    },
    onError: (err: { response?: { data?: { detail?: string } } }) => {
      setMoveError(err.response?.data?.detail || 'Failed to move project')
    },
  })

  const handleMove = (project: PMProject, newStatus: string) => {
    setMoveError('')
    moveStatus.mutate({ id: project.id, status: newStatus })
  }

  const grouped = useMemo(() => {
    const g: Record<string, PMProject[]> = {}
    STATUS_COLUMNS.forEach(s => { g[s] = [] })
    projects.forEach(p => {
      if (g[p.status]) g[p.status].push(p)
    })
    return g
  }, [projects])

  if (settingsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-800" />
      </div>
    )
  }

  if (!settingsLoading && settings?.project_management_enabled !== true) {
    return <Navigate to="/dashboard" replace />
  }

  const isLoading = projectsLoading

  if (projectsError) return (
    <div className="flex flex-col items-center justify-center h-64 gap-3 text-center">
      <div className="text-red-600 font-semibold">Something went wrong</div>
      <div className="text-sm text-slate-500">Unable to load project management data. A database error occurred — please refresh or contact support.</div>
    </div>
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Project Management</h1>
        <div className="flex bg-slate-100 rounded-lg p-0.5">
          <button onClick={() => setView('kanban')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${view === 'kanban' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>
            Kanban
          </button>
          <button onClick={() => setView('timeline')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${view === 'timeline' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>
            Timeline
          </button>
        </div>
      </div>
      <AdvancedFilters activeCount={advancedPmFilterCount} onReset={resetAdvancedPmFilters}>
        <label className="text-xs font-medium text-slate-600">Search project, client, or address
          <input value={search} onChange={e => setSearch(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" placeholder="Search projects" />
        </label>
        <label className="text-xs font-medium text-slate-600">Status
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm"><option value="">All statuses</option>{STATUS_COLUMNS.map(status => <option key={status}>{status}</option>)}</select>
        </label>
        <label className="text-xs font-medium text-slate-600">Project type
          <select value={projectTypeFilter} onChange={e => setProjectTypeFilter(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm"><option value="">All project types</option><option>CLAPJ</option><option>FMTPJ</option><option>ELVPJ</option></select>
        </label>
        <label className="text-xs font-medium text-slate-600">Team leader
          <select value={teamLeaderFilter} onChange={e => setTeamLeaderFilter(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm"><option value="">All team leaders</option>{engineeringUsers.map(user => <option key={user.id} value={user.full_name}>{user.full_name}</option>)}</select>
        </label>
        <label className="text-xs font-medium text-slate-600">Sales person
          <select value={salesPersonFilter} onChange={e => setSalesPersonFilter(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm"><option value="">All sales people</option>{salesUsers.map(user => <option key={user.id} value={user.full_name}>{user.full_name}</option>)}</select>
        </label>
        <label className="text-xs font-medium text-slate-600">Team member
          <select value={teamMemberFilter} onChange={e => setTeamMemberFilter(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm"><option value="">All team members</option>{users.map(user => <option key={user.id} value={user.full_name}>{user.full_name}</option>)}</select>
        </label>
        <label className="text-xs font-medium text-slate-600">Completed projects
          <select value={completedWindow} onChange={e => setCompletedWindow(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm"><option value="recent">Recently completed</option><option value="older">Older completed</option><option value="all">All completed</option></select>
        </label>
        <DateFilter label="Installation from" value={installationFrom} onChange={setInstallationFrom} /><DateFilter label="Installation to" value={installationTo} onChange={setInstallationTo} />
        <DateFilter label="Expected completion from" value={expectedCompletionFrom} onChange={setExpectedCompletionFrom} /><DateFilter label="Expected completion to" value={expectedCompletionTo} onChange={setExpectedCompletionTo} />
        <DateFilter label="Completed from" value={completedFrom} onChange={setCompletedFrom} /><DateFilter label="Completed to" value={completedTo} onChange={setCompletedTo} />
        <DateFilter label="Service expiry from" value={expiryFrom} onChange={setExpiryFrom} /><DateFilter label="Service expiry to" value={expiryTo} onChange={setExpiryTo} />
        <DateFilter label="Created from" value={createdFrom} onChange={setCreatedFrom} /><DateFilter label="Created to" value={createdTo} onChange={setCreatedTo} />
        <DateFilter label="Updated from" value={updatedFrom} onChange={setUpdatedFrom} /><DateFilter label="Updated to" value={updatedTo} onChange={setUpdatedTo} />
      </AdvancedFilters>

      {moveError && (
        <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg border border-red-200">
          <span>{moveError}</span>
          <button onClick={() => setMoveError('')} className="ml-auto"><X size={14} /></button>
        </div>
      )}
      <FilteredEmptyState show={!projectsLoading && projects.length === 0 && advancedPmFilterCount > 0} onReset={resetAdvancedPmFilters} />

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-800" />
        </div>
      ) : view === 'kanban' ? (
        <KanbanView grouped={grouped} userRole={userRole} userName={userName}
          onMove={handleMove} onSelect={setSelected} moving={moveStatus.isPending} />
      ) : (
        <GanttView projects={projects} onSelect={setSelected} />
      )}

      <SlideOver open={!!selected} onClose={() => { setSelected(null); setMoveError('') }}
        title={selected ? `${selected.project_type}-${selected.project_code}` : ''} wide>
        {selected && (
          <QuickPeek project={selected} userRole={userRole} userName={userName}
            onMove={(s) => handleMove(selected, s)} moveError={moveError} moving={moveStatus.isPending}
            onProjectUpdated={(updated) => {
              setSelected(updated)
              queryClient.invalidateQueries({ queryKey: ['pm-projects'] })
            }} />
        )}
      </SlideOver>
    </div>
  )
}

function KanbanView({ grouped, userRole, userName, onMove, onSelect, moving }: {
  grouped: Record<string, PMProject[]>
  userRole: string; userName: string
  onMove: (p: PMProject, s: string) => void
  onSelect: (p: PMProject) => void
  moving: boolean
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
      {STATUS_COLUMNS.map(status => (
        <div key={status} className="bg-slate-50 rounded-xl border border-slate-200 min-h-[200px]">
          <div className={`${STATUS_HEADER_COLORS[status]} text-white px-3 py-2 rounded-t-xl flex items-center justify-between`}>
            <span className="text-sm font-semibold">{status}</span>
            <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">{grouped[status]?.length || 0}</span>
          </div>
          <div className="p-2 space-y-2 max-h-[calc(100vh-340px)] lg:max-h-[calc(100vh-220px)] overflow-y-auto pb-20 lg:pb-2">
            {(grouped[status] || []).map(p => {
              const assigned = parseAssignedTo(p.assigned_to)
              const isComplete = p.status === 'Complete'
              const soon = isCompletingSoon(p)
              const next = getNextStatus(p.status)
              const prev = getPrevStatus(p.status)
              const showForward = next && canMoveForward(userRole, userName, p)
              const showBackward = prev && canMoveBackward(userRole)
              return (
                <div key={p.id}
                  className={`bg-white rounded-lg border border-slate-200 p-3 cursor-pointer hover:shadow-sm transition-shadow ${isComplete ? 'opacity-60' : ''}`}
                  onClick={() => onSelect(p)}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-bold text-slate-900">{p.project_type}-{p.project_code}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${STATUS_COLORS[p.status] || 'bg-slate-100 text-slate-600'}`}>
                      {p.status}
                    </span>
                  </div>
                  <div className="text-xs text-slate-600 mb-1 truncate">{p.client_name}</div>
                  {assigned.team_leader && (
                    <div className="text-[10px] text-slate-400 truncate">TL: {assigned.team_leader}</div>
                  )}
                  {p.installation_start_date && (
                    <div className="text-[10px] text-slate-400 flex items-center gap-1 mt-0.5">
                      <Calendar size={9} /> {p.installation_start_date}
                    </div>
                  )}
                  {soon && (
                    <div className="flex items-center gap-1 mt-1.5">
                      <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 bg-yellow-100 text-yellow-800 rounded-full font-medium border border-yellow-200">
                        <Clock size={8} /> Completing Soon
                      </span>
                    </div>
                  )}
                  {(showForward || showBackward) && (
                    <div className="flex gap-1 mt-2 pt-2 border-t border-slate-100" onClick={e => e.stopPropagation()}>
                      {showBackward && prev && (
                        <button onClick={() => onMove(p, prev)} disabled={moving}
                          className="flex items-center gap-0.5 px-2 py-1 text-[10px] bg-slate-100 text-slate-600 rounded hover:bg-slate-200 disabled:opacity-40">
                          <ChevronLeft size={10} /> {prev}
                        </button>
                      )}
                      {showForward && next && (
                        <button onClick={() => onMove(p, next)} disabled={moving}
                          className="flex items-center gap-0.5 px-2 py-1 text-[10px] bg-red-50 text-red-700 rounded hover:bg-red-100 disabled:opacity-40 ml-auto">
                          {next} <ChevronRight size={10} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
            {(grouped[status] || []).length === 0 && (
              <div className="text-xs text-slate-400 text-center py-6">No projects</div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

function GanttView({ projects, onSelect }: { projects: PMProject[]; onSelect: (p: PMProject) => void }) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [monthOffset, setMonthOffset] = useState(0)

  const baseDate = useMemo(() => {
    const d = new Date()
    d.setDate(1)
    d.setMonth(d.getMonth() + monthOffset)
    return d
  }, [monthOffset])

  const days = useMemo(() => {
    const start = new Date(baseDate)
    start.setDate(start.getDate() - start.getDay())
    const arr: Date[] = []
    for (let i = 0; i < 42; i++) {
      const d = new Date(start)
      d.setDate(d.getDate() + i)
      arr.push(d)
    }
    return arr
  }, [baseDate])

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const startTs = days[0].getTime()
  const endTs = days[days.length - 1].getTime()
  const totalMs = endTs - startTs

  const scheduled = projects.filter(p => p.installation_start_date && p.expected_completion_date)
  const unscheduled = projects.filter(p => !p.installation_start_date || !p.expected_completion_date)

  const DAY_W = 36
  const TOTAL_W = days.length * DAY_W

  const todayOffset = ((today.getTime() - startTs) / totalMs) * TOTAL_W

  const monthLabel = baseDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <button onClick={() => setMonthOffset(m => m - 1)} className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200">
          <ChevronLeft size={16} />
        </button>
        <span className="text-sm font-semibold text-slate-700 min-w-[140px] text-center">{monthLabel}</span>
        <button onClick={() => setMonthOffset(m => m + 1)} className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200">
          <ChevronRight size={16} />
        </button>
        <button onClick={() => setMonthOffset(0)} className="text-xs text-red-700 hover:underline ml-2">Today</button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto" ref={scrollRef}>
          <div style={{ minWidth: TOTAL_W + 200 }}>
            <div className="flex border-b border-slate-200">
              <div className="w-[200px] shrink-0 px-3 py-2 text-xs font-semibold text-slate-500 bg-slate-50 border-r border-slate-200">
                Project
              </div>
              <div className="flex relative">
                {days.map((d, i) => {
                  const isToday = d.toDateString() === today.toDateString()
                  const isSun = d.getDay() === 0
                  return (
                    <div key={i} className={`flex-none text-center border-r border-slate-100 ${isSun ? 'bg-slate-50' : ''}`}
                      style={{ width: DAY_W }}>
                      <div className={`text-[9px] py-1 ${isToday ? 'bg-red-600 text-white font-bold' : 'text-slate-400'}`}>
                        {d.getDate()}{d.getDate() === 1 ? '/' + (d.getMonth() + 1) : ''}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {scheduled.map(p => {
              const assigned = parseAssignedTo(p.assigned_to)
              const start = new Date(p.installation_start_date!)
              const end = new Date(p.expected_completion_date!)
              start.setHours(0, 0, 0, 0)
              end.setHours(0, 0, 0, 0)
              const barLeft = Math.max(0, ((start.getTime() - startTs) / totalMs) * TOTAL_W)
              const barRight = Math.min(TOTAL_W, ((end.getTime() - startTs) / totalMs) * TOTAL_W)
              const barWidth = Math.max(DAY_W, barRight - barLeft)
              const isVisible = start.getTime() <= endTs && end.getTime() >= startTs

              return (
                <div key={p.id} className="flex border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                  onClick={() => onSelect(p)}>
                  <div className="w-[200px] shrink-0 px-3 py-2 border-r border-slate-200">
                    <div className="text-xs font-medium text-slate-900 truncate">{p.project_type}-{p.project_code}</div>
                    <div className="text-[10px] text-slate-400 truncate">{p.client_name}</div>
                  </div>
                  <div className="flex-1 relative" style={{ height: 40 }}>
                    {isVisible && (
                      <div className={`absolute top-1.5 h-5 rounded ${GANTT_BAR_COLORS[p.status] || 'bg-slate-400'} text-white text-[9px] flex items-center px-1.5 truncate group`}
                        style={{ left: barLeft, width: barWidth }}>
                        <span className="truncate">{assigned.team_leader || p.project_code}</span>
                        <div className="absolute bottom-full left-0 mb-1 hidden group-hover:block bg-slate-800 text-white text-[10px] rounded px-2 py-1.5 whitespace-nowrap z-20 shadow-lg">
                          <div className="font-semibold">{p.project_type}-{p.project_code}</div>
                          <div>{p.client_name}</div>
                          {assigned.team_leader && <div>TL: {assigned.team_leader}</div>}
                          {p.team_members && p.team_members.length > 0 && (
                            <div>Team: {p.team_members.map(m => m.full_name).join(', ')}</div>
                          )}
                          <div>{p.installation_start_date} — {p.expected_completion_date}</div>
                        </div>
                      </div>
                    )}
                    {todayOffset > 0 && todayOffset < TOTAL_W && (
                      <div className="absolute top-0 bottom-0 border-l-2 border-dashed border-red-400 z-10"
                        style={{ left: todayOffset }} />
                    )}
                  </div>
                </div>
              )
            })}

            {scheduled.length === 0 && (
              <div className="text-center py-8 text-sm text-slate-400">No scheduled projects to display</div>
            )}
          </div>
        </div>
      </div>

      {unscheduled.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">No Schedule Set ({unscheduled.length})</h3>
          <div className="space-y-1.5">
            {unscheduled.map(p => {
              const assigned = parseAssignedTo(p.assigned_to)
              return (
                <div key={p.id} className="flex items-center gap-3 px-3 py-2 bg-slate-50 rounded-lg text-sm cursor-pointer hover:bg-slate-100"
                  onClick={() => onSelect(p)}>
                  <span className="font-medium text-slate-900">{p.project_type}-{p.project_code}</span>
                  <span className="text-slate-500 truncate flex-1">{p.client_name}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${STATUS_COLORS[p.status] || 'bg-slate-100'}`}>{p.status}</span>
                  {assigned.team_leader && <span className="text-xs text-slate-400">{assigned.team_leader}</span>}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

function QuickPeek({ project, userRole, userName, onMove, moveError, moving, onProjectUpdated }: {
  project: PMProject; userRole: string; userName: string
  onMove: (s: string) => void; moveError: string; moving: boolean
  onProjectUpdated: (updated: PMProject) => void
}) {
  const queryClient = useQueryClient()
  const assigned = parseAssignedTo(project.assigned_to)
  const next = getNextStatus(project.status)
  const prev = getPrevStatus(project.status)
  const showForward = next && canMoveForward(userRole, userName, project)
  const showBackward = prev && canMoveBackward(userRole)
  const canEditFull = isEditRole(userRole)
  const canEditPM = canEditPMProject(userRole, userName, project)
  const isEngineeringUser = ENGINEERING_ROLES.includes(userRole)
  const isTL = isTeamLeaderOf(userName, project)
  const canChangeTL = isEditRole(userRole) || isTL

  const [activeTab, setActiveTab] = useState<'details' | 'site-photos' | 'pre-install' | 'completion'>('details')
  const [isEditing, setIsEditing] = useState(false)
  const [editForm, setEditForm] = useState({
    installation_start_date: project.installation_start_date || '',
    expected_completion_date: project.expected_completion_date || '',
    date_completed: project.date_completed || '',
    service_expiry_date: project.service_expiry_date || '',
    team_leader: assigned.team_leader || '',
    team_members: (project.team_members || []) as { id: number; full_name: string; role: string }[],
    status: project.status,
  })
  const [editError, setEditError] = useState('')

  const { data: userList = [] } = useQuery<SimpleUser[]>({
    queryKey: ['users-list'],
    queryFn: () => api.get('/users/list').then(r => r.data),
    enabled: isEditing && canEditPM,
    staleTime: 120000,
  })

  const engineeringUsers = userList.filter(u => ENGINEERING_ROLES.includes(u.role))

  const saveDetails = useMutation({
    mutationFn: (data: typeof editForm) =>
      api.patch(`/project-management/${project.id}/details`, {
        installation_start_date: data.installation_start_date || null,
        expected_completion_date: data.expected_completion_date || null,
        date_completed: data.date_completed || null,
        service_expiry_date: data.service_expiry_date || null,
        team_leader: canChangeTL ? data.team_leader : undefined,
        team_members: data.team_members,
        status: data.status !== project.status ? data.status : undefined,
      }),
    onSuccess: (res) => {
      setIsEditing(false)
      setEditError('')
      onProjectUpdated(res.data)
      queryClient.invalidateQueries({ queryKey: ['pm-activities', project.id] })
    },
    onError: (err: { response?: { data?: { detail?: string } } }) => {
      setEditError(err.response?.data?.detail || 'Failed to save changes')
    },
  })

  const handleEditOpen = () => {
    const freshAssigned = parseAssignedTo(project.assigned_to)
    setEditForm({
      installation_start_date: project.installation_start_date || '',
      expected_completion_date: project.expected_completion_date || '',
      date_completed: project.date_completed || '',
      service_expiry_date: project.service_expiry_date || '',
      team_leader: freshAssigned.team_leader || '',
      team_members: (project.team_members || []) as { id: number; full_name: string; role: string }[],
      status: project.status,
    })
    setEditError('')
    setIsEditing(true)
  }

  const toggleTeamMember = (u: SimpleUser) => {
    const already = editForm.team_members.find(m => m.id === u.id)
    if (already) {
      setEditForm(f => ({ ...f, team_members: f.team_members.filter(m => m.id !== u.id) }))
    } else {
      setEditForm(f => ({ ...f, team_members: [...f.team_members, { id: u.id, full_name: u.full_name, role: u.role }] }))
    }
  }

  const { data: activities = [], isLoading: activitiesLoading } = useQuery<ActivityEntry[]>({
    queryKey: ['pm-activities', project.id],
    queryFn: () => api.get(`/project-management/${project.id}/activities`).then(r => r.data),
    staleTime: 30000,
  })

  return (
    <div className="space-y-4">
      {/* Tab bar */}
      <div className="flex border-b border-slate-200 -mt-1 overflow-x-auto">
        {[
          { key: 'details' as const, label: 'Details', alwaysShow: true },
          { key: 'site-photos' as const, label: 'Site Photos', alwaysShow: true },
          { key: 'pre-install' as const, label: 'Pre-Install', alwaysShow: userRole !== 'technician' },
          { key: 'completion' as const, label: 'Completion', alwaysShow: true },
        ].filter(t => t.alwaysShow).map(tab => (
          <button
            key={tab.key}
            type="button"
            onClick={() => { setActiveTab(tab.key); setIsEditing(false) }}
            className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap shrink-0 ${
              activeTab === tab.key
                ? 'border-red-700 text-red-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'site-photos' && (
        <ProjectDocumentPanel
          projectId={project.id}
          projectCode={project.project_code}
          projectType={project.project_type}
          assignedTo={project.assigned_to}
          status={project.status}
          dateCompleted={project.date_completed}
          userRole={userRole}
          userName={userName}
          sitePhotos={project.site_photos || []}
          preInstallDocs={project.pre_install_docs || []}
          completionDocs={project.completion_docs || []}
          visibleCategories={['site-photos']}
          context="pm"
          invalidateQueryKeys={[['pm-projects']]}
        />
      )}

      {activeTab === 'pre-install' && userRole !== 'technician' && (
        <ProjectDocumentPanel
          projectId={project.id}
          projectCode={project.project_code}
          projectType={project.project_type}
          assignedTo={project.assigned_to}
          status={project.status}
          dateCompleted={project.date_completed}
          userRole={userRole}
          userName={userName}
          sitePhotos={project.site_photos || []}
          preInstallDocs={project.pre_install_docs || []}
          completionDocs={project.completion_docs || []}
          visibleCategories={['pre-install']}
          context="pm"
          invalidateQueryKeys={[['pm-projects']]}
        />
      )}

      {activeTab === 'completion' && (
        <ProjectDocumentPanel
          projectId={project.id}
          projectCode={project.project_code}
          projectType={project.project_type}
          assignedTo={project.assigned_to}
          status={project.status}
          dateCompleted={project.date_completed}
          userRole={userRole}
          userName={userName}
          sitePhotos={project.site_photos || []}
          preInstallDocs={project.pre_install_docs || []}
          completionDocs={project.completion_docs || []}
          visibleCategories={['completion']}
          context="pm"
          invalidateQueryKeys={[['pm-projects']]}
        />
      )}

      {activeTab === 'details' && (!isEditing ? (
        <>
          <div className="grid grid-cols-2 gap-3">
            {[
              ['Project Code', `${project.project_type}-${project.project_code}`],
              ['Client', project.client_name],
              ['Team Leader', assigned.team_leader],
              ['Sales Person', assigned.sales_person],
              ['Install Start', formatDateOnly(project.installation_start_date)],
              ['Expected Completion', formatDateOnly(project.expected_completion_date)],
              ['Date Completed', formatDateOnly(project.date_completed)],
              ['Service Expiry', formatDateOnly(project.service_expiry_date)],
            ].map(([l, v]) => (
              <div key={l as string} className="text-sm">
                <span className="text-xs text-slate-500 block">{l}</span>
                <span className="font-medium text-slate-900">{v || '—'}</span>
              </div>
            ))}
            <div className="text-sm">
              <span className="text-xs text-slate-500 block">Status</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[project.status] || 'bg-slate-100'}`}>
                {project.status}
              </span>
            </div>
          </div>
          <AuditInfo
            createdByName={project.created_by_name}
            createdAt={project.created_at}
            updatedAt={project.updated_at}
          />

          {project.address && (
            <div className="text-sm">
              <span className="text-xs text-slate-500 block">Address</span>
              <p className="text-slate-700">{project.address}</p>
            </div>
          )}

          {Array.isArray(project.team_members) && project.team_members.length > 0 && (
            <div className="text-sm">
              <span className="text-xs text-slate-500 block mb-1">Team Members</span>
              <div className="flex flex-wrap gap-1.5">
                {project.team_members.map(m => (
                  <span key={m.id} className="inline-flex items-center px-2 py-0.5 bg-blue-50 text-blue-800 rounded-full text-xs font-medium">
                    {m.full_name} <span className="text-blue-400 ml-1">{ROLE_SHORT[m.role] || m.role}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Edit2 size={14} className="text-red-700" /> Edit Project Details
          </h3>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500 block mb-1">Install Start Date</label>
              <input type="date" value={editForm.installation_start_date}
                onChange={e => setEditForm(f => ({ ...f, installation_start_date: e.target.value }))}
                className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Expected Completion</label>
              <input type="date" value={editForm.expected_completion_date}
                onChange={e => setEditForm(f => ({ ...f, expected_completion_date: e.target.value }))}
                className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Date Completed</label>
              <input type="date" value={editForm.date_completed}
                onChange={e => setEditForm(f => ({ ...f, date_completed: e.target.value }))}
                className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Service Expiry Date</label>
              <input type="date" value={editForm.service_expiry_date}
                onChange={e => setEditForm(f => ({ ...f, service_expiry_date: e.target.value }))}
                className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700" />
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-500 block mb-1">Status</label>
            <select value={editForm.status}
              onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}
              className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700 bg-white">
              {STATUS_COLUMNS.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {canChangeTL && (
            <div>
              <label className="text-xs text-slate-500 block mb-1 flex items-center gap-1">
                <User size={10} /> Team Leader
              </label>
              <select value={editForm.team_leader}
                onChange={e => setEditForm(f => ({ ...f, team_leader: e.target.value }))}
                className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700 bg-white">
                <option value="">— Unassigned —</option>
                {engineeringUsers.map(u => (
                  <option key={u.id} value={u.full_name}>{u.full_name} ({ROLE_SHORT[u.role] || u.role})</option>
                ))}
              </select>
              {isTL && editForm.team_leader !== userName && editForm.team_leader !== '' && (
                <p className="text-[11px] text-amber-600 mt-1">
                  You will lose edit access after transferring the team leader role.
                </p>
              )}
            </div>
          )}

          <div>
            <label className="text-xs text-slate-500 block mb-1">Team Members</label>
            {engineeringUsers.length === 0 ? (
              <p className="text-xs text-slate-400 italic">Loading users...</p>
            ) : (
              <div className="border border-slate-200 rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                {engineeringUsers.map(u => {
                  const sel = editForm.team_members.some(m => m.id === u.id)
                  return (
                    <button key={u.id} type="button" onClick={() => toggleTeamMember(u)}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left border-b border-slate-100 last:border-b-0 transition-colors ${sel ? 'bg-blue-50 text-blue-900' : 'hover:bg-slate-50 text-slate-700'}`}>
                      <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${sel ? 'bg-blue-600 border-blue-600' : 'border-slate-300'}`}>
                        {sel && <Check size={10} className="text-white" />}
                      </div>
                      <span className="flex-1">{u.full_name}</span>
                      <span className="text-xs text-slate-400">{ROLE_SHORT[u.role] || u.role}</span>
                    </button>
                  )
                })}
              </div>
            )}
            {editForm.team_members.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {editForm.team_members.map(m => (
                  <span key={m.id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs">
                    {m.full_name}
                    <button onClick={() => setEditForm(f => ({ ...f, team_members: f.team_members.filter(x => x.id !== m.id) }))}>
                      <X size={10} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {editError && (
            <div className="bg-red-50 text-red-700 text-xs px-3 py-2 rounded-lg border border-red-200">{editError}</div>
          )}

          <div className="flex gap-2">
            <button onClick={() => saveDetails.mutate(editForm)} disabled={saveDetails.isPending}
              className="flex items-center gap-1.5 px-4 py-2 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50">
              {saveDetails.isPending ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
              Save Changes
            </button>
            <button onClick={() => { setIsEditing(false); setEditError('') }} disabled={saveDetails.isPending}
              className="px-4 py-2 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 disabled:opacity-50">
              Cancel
            </button>
          </div>
        </div>
      ))}

      {activeTab === 'details' && <div className="pt-3 border-t border-slate-200">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Recent Changes</span>
        {activitiesLoading ? (
          <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
            <div className="animate-spin rounded-full h-3 w-3 border-b border-slate-400" /> Loading…
          </div>
        ) : activities.length === 0 ? (
          <p className="mt-2 text-xs text-slate-400">No activity yet</p>
        ) : (
          <div className="mt-2 space-y-2">
            {activities.slice(0, 5).map(a => (
              <div key={a.id} className="flex items-start gap-2">
                <div className="mt-0.5 h-1.5 w-1.5 rounded-full bg-slate-300 shrink-0 mt-1.5" />
                <div className="min-w-0">
                  <p className="text-xs text-slate-700 leading-snug">{a.details || a.action}</p>
                  <p className="text-[10px] text-slate-400">{a.full_name} · {timeAgo(a.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>}

      {activeTab === 'details' && moveError && (
        <div className="bg-red-50 text-red-700 text-xs px-3 py-2 rounded-lg border border-red-200">{moveError}</div>
      )}

      {activeTab === 'details' && (showForward || showBackward) && (
        <div className="flex gap-2 pt-3 border-t border-slate-200">
          {showBackward && prev && (
            <button onClick={() => onMove(prev)} disabled={moving}
              className="flex items-center gap-1 px-3 py-2 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 disabled:opacity-40">
              <ChevronLeft size={14} /> Move to {prev}
            </button>
          )}
          {showForward && next && (
            <button onClick={() => onMove(next)} disabled={moving}
              className="flex items-center gap-1 px-3 py-2 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50 ml-auto">
              Move to {next} <ArrowRight size={14} />
            </button>
          )}
        </div>
      )}

      {activeTab === 'details' && (
        <div className="pt-3 border-t border-slate-200 flex flex-wrap gap-2 items-center">
          {canEditPM && !isEditing && (
            <button onClick={handleEditOpen}
              className="inline-flex items-center gap-1.5 text-sm text-red-700 hover:text-red-900 font-medium bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg transition-colors">
              <Edit2 size={13} /> Edit Schedule & Team
            </button>
          )}
          {canEditFull && !isEngineeringUser && (
            <a href={`/customers?open=${project.id}`}
              className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 font-medium">
              Full Edit in Project Bank <ExternalLink size={12} />
            </a>
          )}
        </div>
      )}

      <CommentsSection entityType="project" entityId={project.id} />
    </div>
  )
}
